/**
 * Role-based Access Control (RBAC) Utilities
 * 
 * This module provides a scalable way to manage user permissions across the application.
 * Add new roles and permissions here to extend the system.
 */

// Available roles in the system
export type Role = 'admin' | 'employee' | 'company_rep';

// User interface with company info
export interface User {
  id: number;
  email: string;
  roles: Role[];
  company_id?: number | null;
  company?: {
    id: number;
    name: string;
  };
}

// Permission types
export type Permission = 
  | 'read'
  | 'write'
  | 'create'
  | 'update'
  | 'delete';

// Resource types in the application
export type Resource =
  | 'programs'
  | 'received_notes'
  | 'deliveries'
  | 'invoices'
  | 'employees'
  | 'employee_shifts'
  | 'machines'
  | 'companies'
  | 'users'
  | 'metrics'
  | 'inventory'
  | 'finance'
  | 'audit_log'
  | 'settings';

// Define which resources each role can access and what actions they can perform
const rolePermissions: Record<Role, Partial<Record<Resource, Permission[]>>> = {
  admin: {
    programs: ['read', 'write', 'create', 'update', 'delete'],
    received_notes: ['read', 'write', 'create', 'update', 'delete'],
    deliveries: ['read', 'write', 'create', 'update', 'delete'],
    invoices: ['read', 'write', 'create', 'update', 'delete'],
    employees: ['read', 'write', 'create', 'update', 'delete'],
    employee_shifts: ['read', 'write', 'create', 'update', 'delete'],
    machines: ['read', 'write', 'create', 'update', 'delete'],
    companies: ['read', 'write', 'create', 'update', 'delete'],
    users: ['read', 'write', 'create', 'update', 'delete'],
    metrics: ['read'],
    inventory: ['read'],
    finance: ['read', 'write', 'create', 'update', 'delete'],
    audit_log: ['read'],
    settings: ['read', 'write', 'create', 'update', 'delete'],
  },
  employee: {
    programs: ['read', 'write', 'create', 'update', 'delete'],
    received_notes: ['read', 'write', 'create', 'update', 'delete'],
    deliveries: ['read', 'write', 'create', 'update', 'delete'],
    invoices: ['read', 'write', 'create', 'update', 'delete'],
    employees: ['read', 'write', 'create', 'update', 'delete'],
    employee_shifts: ['read', 'write', 'create', 'update', 'delete'],
    machines: ['read', 'write', 'create', 'update', 'delete'],
    companies: ['read', 'write', 'create', 'update', 'delete'],
    metrics: ['read'],
    inventory: ['read'],
    finance: ['read'],
    settings: ['read'],
  },
  company_rep: {
    // company_rep can only READ their own company's data
    programs: ['read'],
    received_notes: ['read'],
    deliveries: ['read'],
    invoices: ['read'],
  },
};

// Get the current user from localStorage
export function getCurrentUser(): User | null {
  try {
    const userJson = localStorage.getItem('user');
    if (!userJson) return null;
    return JSON.parse(userJson) as User;
  } catch {
    return null;
  }
}

// Check if user has a specific role
export function hasRole(user: User | null, role: Role): boolean {
  if (!user || !user.roles) return false;
  return user.roles.includes(role);
}

// Check if user has any of the given roles
export function hasAnyRole(user: User | null, roles: Role[]): boolean {
  if (!user || !user.roles) return false;
  return roles.some(role => user.roles.includes(role));
}

// Check if user is admin
export function isAdmin(user: User | null): boolean {
  return hasRole(user, 'admin');
}

// Check if user is employee
export function isEmployee(user: User | null): boolean {
  return hasRole(user, 'employee');
}

// Check if user is company_rep (and not admin or employee)
export function isCompanyRep(user: User | null): boolean {
  return hasRole(user, 'company_rep') && !isAdmin(user) && !isEmployee(user);
}

// Check if user is read-only (company_rep only)
export function isReadOnly(user: User | null): boolean {
  return isCompanyRep(user);
}

// Check if user can write (not read-only)
export function canWrite(user: User | null): boolean {
  return !isReadOnly(user);
}

// Check if user has permission for a specific action on a resource
export function hasPermission(
  user: User | null,
  resource: Resource,
  permission: Permission
): boolean {
  if (!user || !user.roles) return false;
  
  // Check each role the user has
  for (const role of user.roles as Role[]) {
    const resourcePerms = rolePermissions[role]?.[resource];
    if (resourcePerms && resourcePerms.includes(permission)) {
      return true;
    }
  }
  
  return false;
}

// Check if user can access a resource (has at least read permission)
export function canAccessResource(user: User | null, resource: Resource): boolean {
  return hasPermission(user, resource, 'read');
}

// Check if user can modify a resource (has write permission)
export function canModifyResource(user: User | null, resource: Resource): boolean {
  return hasPermission(user, resource, 'write');
}

// Check if user can create on a resource
export function canCreate(user: User | null, resource: Resource): boolean {
  return hasPermission(user, resource, 'create');
}

// Check if user can update on a resource
export function canUpdate(user: User | null, resource: Resource): boolean {
  return hasPermission(user, resource, 'update');
}

// Check if user can delete on a resource
export function canDelete(user: User | null, resource: Resource): boolean {
  return hasPermission(user, resource, 'delete');
}

// Get display name for a role
export function getRoleDisplayName(role: Role): string {
  const displayNames: Record<Role, string> = {
    admin: 'Admin',
    employee: 'Employee',
    company_rep: 'Company Representative',
  };
  return displayNames[role] || role;
}

// Get available roles
export function getAvailableRoles(): Role[] {
  return ['admin', 'employee', 'company_rep'];
}

// Hook-friendly version: use with React components
export function usePermissions() {
  const user = getCurrentUser();
  
  return {
    user,
    isAdmin: isAdmin(user),
    isEmployee: isEmployee(user),
    isCompanyRep: isCompanyRep(user),
    isReadOnly: isReadOnly(user),
    canWrite: canWrite(user),
    hasRole: (role: Role) => hasRole(user, role),
    hasAnyRole: (roles: Role[]) => hasAnyRole(user, roles),
    hasPermission: (resource: Resource, permission: Permission) => 
      hasPermission(user, resource, permission),
    canAccessResource: (resource: Resource) => canAccessResource(user, resource),
    canModifyResource: (resource: Resource) => canModifyResource(user, resource),
    canCreate: (resource: Resource) => canCreate(user, resource),
    canUpdate: (resource: Resource) => canUpdate(user, resource),
    canDelete: (resource: Resource) => canDelete(user, resource),
  };
}
